/**
 * Opening WhatsApp, a dialler and a mail composer.
 *
 * Every one of these can fail for a reason that is not an error: WhatsApp may
 * not be installed, a tablet may have no dialler, a desktop browser may have no
 * mail client. So each returns whether it worked rather than throwing, and
 * WhatsApp falls back to wa.me in a browser — which works everywhere and is
 * what the web build needs anyway.
 *
 * Nothing here hardcodes a number. All three come from server configuration, so
 * the charity can change who answers without shipping a build.
 */

import { Linking, Platform } from 'react-native';

/** Strip spaces, dashes and brackets; wa.me wants digits only, no leading +. */
function digitsOnly(value: string): string {
  return value.replace(/[^\d]/g, '');
}

async function open(url: string): Promise<boolean> {
  try {
    // canOpenURL is unreliable on web and on Android without a queries entry,
    // so this attempts the open and treats a throw as "not available".
    await Linking.openURL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open a WhatsApp conversation.
 *
 * Tries the app's own scheme first on a phone, then falls back to wa.me, which
 * opens WhatsApp when installed and WhatsApp Web when not.
 */
export async function openWhatsApp(number: string, message?: string): Promise<boolean> {
  const phone = digitsOnly(number);
  if (!phone) return false;
  const text = message ? `?text=${encodeURIComponent(message)}` : '';

  if (Platform.OS !== 'web' && (await open(`whatsapp://send?phone=${phone}${text && `&text=${encodeURIComponent(message ?? '')}`}`))) {
    return true;
  }
  return open(`https://wa.me/${phone}${text}`);
}

export async function openDialler(number: string): Promise<boolean> {
  const phone = number.replace(/[^\d+]/g, '');
  if (!phone) return false;
  return open(`tel:${phone}`);
}

export async function openMail(address: string, subject?: string): Promise<boolean> {
  if (!address.trim()) return false;
  const query = subject ? `?subject=${encodeURIComponent(subject)}` : '';
  return open(`mailto:${address.trim()}${query}`);
}

/**
 * Open the charity's own site.
 *
 * An operator may well type "meal-donater.vercel.app" without a scheme, and a
 * URL with no scheme opens nothing at all; https is assumed rather than
 * silently doing nothing.
 */
export async function openWebsite(url: string): Promise<boolean> {
  const trimmed = url.trim();
  if (!trimmed) return false;
  const absolute = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  return open(absolute);
}
